import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import AssessmentQuestionBuilder, { normalizeQuestions } from './AssessmentQuestionBuilder';
import './AssessmentEditor.css';
import EditorSettings from './EditorSettings';

const TWO_WAY_VOICE = 'two-way-voice';

/**
 * Produce the browser-side approval hash expected by the backend.
 * IDs, order, six-goal limit, and whitespace normalization must stay identical
 * to `ai_interview_routes.question_content_hash`; saving changed text therefore
 * creates a new explicit employer approval rather than reusing stale metadata.
 */
const questionContentHash = async (questions) => {
  const canonical = questions
    .filter((question) => String(question.text || '').trim())
    .slice(0, 6)
    .map((question, index) => ({
      id: String(question.id || `goal-${index + 1}`),
      text: String(question.text || '').trim().replace(/\s+/g, ' ')
    }));
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(canonical)));
  return Array.from(new Uint8Array(digest)).map((value) => value.toString(16).padStart(2, '0')).join('');
};

const AssessmentEditor = () => {
  const { assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const jobIdFromUrl = searchParams.get('job');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();

  const [companyUID, setCompanyUID] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [assessment, setAssessment] = useState({ title: '', duration_minutes: 30, questions: [] });
  const [isAiInterview, setIsAiInterview] = useState(false);

  const [isJobVarying, setIsJobVarying] = useState(false);
  const [isDepartmentPreview, setIsDepartmentPreview] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(jobIdFromUrl || '');
  const [jobOptions, setJobOptions] = useState([]);
  const [fullAssessmentData, setFullAssessmentData] = useState(null);

  useEffect(() => {
    const resolveCompany = async () => {
      if (!currentUser) return;
      try {
        const ownerSnap = await get(ref(database, `companies/${currentUser.uid}`));
        if (ownerSnap.exists()) {
          setCompanyUID(currentUser.uid);
          return;
        }
        const collabSnap = await get(ref(database, `collaborators/${currentUser.uid}`));
        if (collabSnap.exists()) {
          setCompanyUID(collabSnap.val().companyId);
        }
      } catch (e) {
        console.error('Error resolving company for assessment editor:', e);
      }
    };
    resolveCompany();
  }, [currentUser]);

  // Load
  useEffect(() => {
    const load = async () => {
      if (!companyUID) return;
      try {
        const snap = await get(ref(database, `companies/${companyUID}/assessments/${assessmentId}`));
        if (snap.exists()) {
          const data = snap.val();
          if (data?.deliveryType === TWO_WAY_VOICE && data?.answerFormat === 'live-voice') {
            navigate(`/interview-editor/${assessmentId}${jobIdFromUrl ? `?job=${jobIdFromUrl}` : ''}`, { replace: true });
            return;
          }
          setFullAssessmentData(data);
          const aiInterviewMode = data?.mode === 'ai-interview' || data?.type === 'ai-interview';
          setIsAiInterview(aiInterviewMode);
          if (data.varyByJob) {
            setIsJobVarying(true);
            const jobQuestions = data.jobQuestions || {};
            const opts = Object.entries(jobQuestions).map(([id, d]) => ({
              id,
              title: d.jobTitle || id,
              department: d.jobDepartment || ''
            }));
            setJobOptions(opts);
            const requestedJobExists = Boolean(jobIdFromUrl && jobQuestions[jobIdFromUrl]);
            const jobToLoad = requestedJobExists ? jobIdFromUrl : (opts[0]?.id || '');
            setSelectedJobId(jobToLoad);
            const jobData = jobQuestions[jobToLoad];
            const previewData = data.previewAssessment
              || (Array.isArray(data.questions) && data.questions.length ? data : null);
            const showDepartmentPreview = !jobData;
            setIsDepartmentPreview(showDepartmentPreview);
            setAssessment({
              title: data.title,
              duration_minutes: jobData?.duration_minutes || previewData?.duration_minutes || data.duration_minutes || 30,
              questions: normalizeQuestions(jobData?.questions || previewData?.questions || [], aiInterviewMode && data.deliveryType !== TWO_WAY_VOICE),
              assessment_prompt: data.assessment_prompt || data.baseConfig?.assessmentPrompt || '',
              grading_rubric: data.grading_rubric || data.baseConfig?.gradingRubric || ''
            });
          } else {
            setAssessment({
              ...data,
              questions: normalizeQuestions(data.questions || [], aiInterviewMode && data.deliveryType !== TWO_WAY_VOICE)
            });
          }
        }
      } catch (e) {
        console.error('Error loading assessment:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [assessmentId, companyUID, jobIdFromUrl, navigate]);

  const handleJobChange = (newJobId) => {
    setSelectedJobId(newJobId);
    setIsDepartmentPreview(false);
    if (fullAssessmentData?.jobQuestions?.[newJobId]) {
      const d = fullAssessmentData.jobQuestions[newJobId];
      setAssessment(prev => ({
        ...prev,
        duration_minutes: d.duration_minutes || fullAssessmentData?.duration_minutes || 30,
        questions: normalizeQuestions(d.questions || [], isAiInterview && fullAssessmentData?.deliveryType !== TWO_WAY_VOICE)
      }));
    }
    navigate(`/assessment-editor/${assessmentId}?job=${newJobId}`, { replace: true });
  };

  const save = async () => {
    setSaving(true);
    try {
      const normalizedQuestions = normalizeQuestions(
        assessment.questions,
        isAiInterview && fullAssessmentData?.deliveryType !== TWO_WAY_VOICE
      ).slice(0, fullAssessmentData?.deliveryType === TWO_WAY_VOICE ? 6 : undefined);
      const isTwoWayVoice = fullAssessmentData?.deliveryType === TWO_WAY_VOICE;
      const approvedDuration = isTwoWayVoice ? Math.min(20, Math.max(1, assessment.duration_minutes)) : assessment.duration_minutes;
      const approval = isTwoWayVoice ? {
        status: 'approved',
        approvalStatus: 'approved',
        approvedAt: new Date().toISOString(),
        approvedBy: currentUser.uid,
        contentHash: await questionContentHash(normalizedQuestions)
      } : {};
      if (isJobVarying && selectedJobId) {
        const selectedJob = jobOptions.find(j => j.id === selectedJobId);
        await set(ref(database, `companies/${companyUID}/assessments/${assessmentId}/jobQuestions/${selectedJobId}`), {
          questions: normalizedQuestions,
          duration_minutes: approvedDuration,
          generatedAt: new Date().toISOString(),
          jobTitle: selectedJob?.title || selectedJobId,
          jobDepartment: selectedJob?.department || '',
          ...(isTwoWayVoice ? {
            status: approval.status,
            approvedAt: approval.approvedAt,
            approvedBy: approval.approvedBy,
            contentHash: approval.contentHash
          } : {})
        });
        await set(ref(database, `companies/${companyUID}/assessments/${assessmentId}/title`), assessment.title);
        await update(ref(database, `companies/${companyUID}/assessments/${assessmentId}`), {
          assessment_prompt: assessment.assessment_prompt || '',
          grading_rubric: assessment.grading_rubric || ''
        });
        setFullAssessmentData(prev => ({
          ...prev, title: assessment.title,
          jobQuestions: {
            ...prev.jobQuestions,
            [selectedJobId]: {
              ...prev.jobQuestions[selectedJobId],
              questions: normalizedQuestions,
              duration_minutes: approvedDuration,
              ...(isTwoWayVoice ? approval : {})
            }
          }
        }));
      } else if (isJobVarying) {
        const previewAssessment = {
          ...(fullAssessmentData?.previewAssessment || {}),
          questions: normalizedQuestions,
          duration_minutes: approvedDuration,
          generatedAt: new Date().toISOString(),
          scopeDepartments: fullAssessmentData?.departments || fullAssessmentData?.baseConfig?.departments || []
        };
        await update(ref(database, `companies/${companyUID}/assessments/${assessmentId}`), {
          title: assessment.title,
          assessment_prompt: assessment.assessment_prompt || '',
          grading_rubric: assessment.grading_rubric || '',
          previewAssessment
        });
        setFullAssessmentData(prev => ({
          ...prev,
          title: assessment.title,
          assessment_prompt: assessment.assessment_prompt || '',
          grading_rubric: assessment.grading_rubric || '',
          previewAssessment
        }));
      } else {
        await set(ref(database, `companies/${companyUID}/assessments/${assessmentId}`), {
          ...assessment,
          duration_minutes: approvedDuration,
          questions: normalizedQuestions,
          ...(isTwoWayVoice ? {
            baseConfig: {
              ...(fullAssessmentData?.baseConfig || assessment.baseConfig || {}),
              approvalStatus: approval.approvalStatus,
              approvedAt: approval.approvedAt,
              approvedBy: approval.approvedBy,
              contentHash: approval.contentHash,
              followUpLimit: 2,
              interviewVersion: 2
            }
          } : {})
        });
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      console.error('Error saving:', e);
      alert(t('assessmentEditor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ae-loading" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <i className="fa-solid fa-spinner fa-spin"></i>
        <p>{t('assessmentEditor.loading')}</p>
      </div>
    );
  }

  return (
    <div className="ae-shell" style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: isRTL ? 'Alexandria, Montserrat, sans-serif' : 'Montserrat, sans-serif' }}>

      {/* Back row */}
      <div className="ae-back-row">
        <button className="ae-back-btn" onClick={() => navigate('/applicants')}>
          <i className={isRTL ? 'fa-solid fa-arrow-right' : 'fa-solid fa-arrow-left'}></i>
          {t('assessmentEditor.close')}
        </button>
      </div>

      {/* Content column */}
      <div className="ae-content">

        {/* Header */}
        <div className="ae-header">
          <div className="ae-header-title">
            <h2>
              <i className={`fa-solid ${isAiInterview && fullAssessmentData?.deliveryType === TWO_WAY_VOICE ? 'fa-robot' : isAiInterview ? 'fa-video' : 'fa-robot'}`}></i>
              {isAiInterview
                ? (t('assessmentEditor.aiInterviewTitle') || 'AI Interview Editor')
                : t('assessmentEditor.title')
              }
            </h2>
          </div>
          <div className="ae-header-actions">
            {!isDepartmentPreview && (
              <button className="ae-btn-secondary" onClick={() => window.open(`/take-assessment/${assessmentId}`, '_blank')}>
                <i className="fa-solid fa-eye"></i>
                {t('assessmentEditor.preview')}
              </button>
            )}
            <button className="ae-btn-primary" onClick={save} disabled={saving}>
              {saving
                ? <><i className="fa-solid fa-spinner fa-spin"></i> {t('assessmentEditor.saving') || 'Saving…'}</>
                : savedFlash
                  ? <><i className="fa-solid fa-check"></i> {t('assessmentEditor.saved') || 'Saved'}</>
                  : <><i className="fa-solid fa-floppy-disk"></i> {t('assessmentEditor.save')}</>
              }
            </button>
            <EditorSettings title={t('assessmentEditor.settings')} t={t}>
              <div className="ae-meta-section">
                <div>
                  <label className="ae-field-label">{t('assessmentEditor.titleLabel')}</label>
                  <input
                    type="text"
                    className="ae-input"
                    value={assessment.title}
                    onChange={e => setAssessment(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={t('assessmentEditor.titlePlaceholder')}
                  />
                </div>

                {isJobVarying && jobOptions.length > 0 && (
                  <div>
                    <label className="ae-field-label">{t('assessmentEditor.jobVariation')}</label>
                    <select className="ae-select" style={{ width: '100%' }} value={selectedJobId} onChange={e => handleJobChange(e.target.value)}>
                      {jobOptions.map(j => (
                        <option key={j.id} value={j.id}>
                          {j.department ? `${j.title} — ${j.department}` : j.title}
                        </option>
                      ))}
                    </select>
                    <p className="ae-inline-note">
                      <i className="fa-solid fa-circle-info"></i>
                      {t('assessmentEditor.jobVariationHelp')}
                    </p>
                  </div>
                )}

                {isDepartmentPreview && (
                  <div className="ae-preview-notice" role="status">
                    <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
                    <div>
                      <strong>{t('assessmentEditor.departmentPreviewTitle')}</strong>
                      <span>{t('assessmentEditor.departmentPreviewBody')}</span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="ae-field-label">{t('assessmentEditor.durationLabel')}</label>
                  <input
                    type="number"
                    min="1"
                    max={fullAssessmentData?.deliveryType === TWO_WAY_VOICE ? 20 : undefined}
                    className="ae-input ae-input-short"
                    value={assessment.duration_minutes}
                    onChange={e => setAssessment(prev => ({ ...prev, duration_minutes: parseInt(e.target.value, 10) || 0 }))}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                    <label className="ae-field-label" htmlFor="assessment-editor-brief">
                      {t('assessmentEditor.assessmentPrompt')}
                    </label>
                    <textarea
                      id="assessment-editor-brief"
                      className="ae-textarea"
                      rows={4}
                      value={assessment.assessment_prompt || ''}
                      onChange={event => setAssessment(prev => ({ ...prev, assessment_prompt: event.target.value }))}
                      placeholder={t('assessmentEditor.assessmentPromptPlaceholder')}
                    />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                    <label className="ae-field-label" htmlFor="assessment-editor-rubric">
                      <i className="fa-solid fa-lock" style={{ marginInlineEnd: '6px' }}></i>
                      {t('assessmentEditor.gradingRubric')}
                    </label>
                    <p className="ae-inline-note">{t('assessmentEditor.gradingRubricHelp')}</p>
                    <textarea
                      id="assessment-editor-rubric"
                      className="ae-textarea"
                      rows={5}
                      value={assessment.grading_rubric || ''}
                      onChange={event => setAssessment(prev => ({ ...prev, grading_rubric: event.target.value }))}
                      placeholder={t('assessmentEditor.gradingRubricPlaceholder')}
                    />
                </div>
              </div>
            </EditorSettings>
          </div>
        </div>

        {/* Questions section */}
        <AssessmentQuestionBuilder
          questions={assessment.questions}
          onChange={(questions) => setAssessment((prev) => ({ ...prev, questions }))}
          t={t}
          isAiInterview={isAiInterview}
          isOneWayInterviewerBuilder={isAiInterview && fullAssessmentData?.deliveryType !== TWO_WAY_VOICE}
          videoBannerText={fullAssessmentData?.deliveryType === TWO_WAY_VOICE
            ? (t('assessmentEditor.twoWayQuestionApproval') || 'Check these questions for this job. Saving makes them ready for every candidate applying to this job.')
            : undefined}
        />
      </div>
    </div>
  );
};

export default AssessmentEditor;
